'use client'
import type { Where } from 'payload'

import { useListQuery } from '@payloadcms/ui'

import { statiOrdine, statiPagamento } from '@/negozio/ordini/stati'

import './ordini.scss'

type Campo = 'stato' | 'statoPagamento'

/**
 * The list query as "OR of AND groups", the shape the Filters panel writes
 * (`{ or: [{ and: [...] }] }`), whatever shape it arrived in.
 */
const gruppi = (where?: Where): Where[][] => {
  if (!where || !Object.keys(where).length) return [[]]
  if (Array.isArray(where.or)) return where.or.map((g) => (Array.isArray(g.and) ? g.and : [g]))
  if (Array.isArray(where.and)) return [where.and]
  return [[where]]
}

/** Replaces any condition on `campo` in every group with `campo = valore` (or none). */
const conFiltro = (where: Where | undefined, campo: Campo, valore: string | null): Where => {
  const or = gruppi(where).map((and) => {
    const resto = and.filter((c) => !(campo in c))
    return { and: valore ? [...resto, { [campo]: { equals: valore } }] : resto }
  })
  return or.every(({ and }) => !and.length) ? {} : { or }
}

const valoreAttivo = (where: Where | undefined, campo: Campo): string | null => {
  const condizione = gruppi(where)[0]?.find((c) => campo in c)?.[campo] as
    { equals?: unknown } | undefined
  return typeof condizione?.equals === 'string' ? condizione.equals : null
}

/**
 * `beforeListTable` of `ordini`: one-click filters on order and payment state. They write a
 * normal list query, so they show up (and can be refined) in the Filters panel too.
 */
export const FiltriRapidi: React.FC = () => {
  const { query, refineListData } = useListQuery()

  const riga = (
    campo: Campo,
    etichetta: string,
    opzioni: readonly { label: string; value: string }[],
  ) => {
    const attivo = valoreAttivo(query.where, campo)
    const pulsante = (valore: string | null, label: string) => (
      <button
        aria-pressed={attivo === valore}
        className="filtri-ordini__pulsante"
        data-stato={valore ?? undefined}
        key={valore ?? 'tutti'}
        onClick={() =>
          void refineListData({ where: conFiltro(query.where, campo, valore), page: 1 })
        }
        type="button"
      >
        {label}
      </button>
    )
    return (
      <div aria-label={etichetta} className="filtri-ordini__riga" role="group">
        <span className="filtri-ordini__etichetta">{etichetta}</span>
        {pulsante(null, 'Tutti')}
        {opzioni.map(({ label, value }) => pulsante(value, label))}
      </div>
    )
  }

  return (
    <div className="filtri-ordini">
      {riga('stato', 'Stato', statiOrdine)}
      {riga('statoPagamento', 'Pagamento', statiPagamento)}
    </div>
  )
}
